// SendFormModal.js - Skicka ett formulär (t.ex. EQ-5D) till patienten via Bokningsappen
import React, { useState, useEffect } from 'react';
import apiClient from '../../apiClient';
import { formatPnrPasis } from '../../utils/personnummer';
import './PatientPortal.css';

const SendFormModal = ({ patient, onClose, onSent }) => {
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [formRef, setFormRef] = useState('');
  const [notify, setNotify] = useState(patient?.phone ? 'sms' : (patient?.email ? 'email' : 'none'));
  const [phone, setPhone] = useState(patient?.phone || '');
  const [email, setEmail] = useState(patient?.email || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const resp = await apiClient.get('/api/patient-portal/templates');
        const list = resp.data.templates || [];
        setTemplates(list);
        if (list.length > 0) setFormRef(list[0].form_ref);
      } catch (err) {
        console.error('Error fetching form templates:', err);
        setError(err.response?.data?.error || 'Kunde inte hämta formulärmallar');
      } finally {
        setLoadingTemplates(false);
      }
    };
    fetchTemplates();
  }, []);

  const selected = templates.find(t => t.form_ref === formRef);

  const handleSend = async () => {
    if (!formRef) {
      setError('Välj ett formulär');
      return;
    }
    if (notify === 'sms' && !phone.trim()) {
      setError('Ange ett telefonnummer eller välj en annan notiskanal');
      return;
    }
    if (notify === 'email' && !email.trim()) {
      setError('Ange en e-postadress eller välj en annan notiskanal');
      return;
    }

    try {
      setSending(true);
      setError(null);
      const resp = await apiClient.post('/api/patient-portal/items', {
        patient_id: patient.patient_id,
        form_ref: formRef,
        notify,
        phone: notify === 'sms' ? phone : undefined,
        email: notify === 'email' ? email : undefined,
      });
      setResult(resp.data);
      onSent?.(resp.data.item);
    } catch (err) {
      console.error('Error sending form:', err);
      setError(err.response?.data?.error || 'Kunde inte skicka formuläret');
    } finally {
      setSending(false);
    }
  };

  const notification = result?.notification;

  return (
    <div className="portal-modal-overlay" onClick={onClose}>
      <div className="portal-modal" onClick={e => e.stopPropagation()}>
        <div className="portal-modal-header">
          <h3><i className="fas fa-clipboard-list"></i> Skicka formulär</h3>
          <button className="close-btn" onClick={onClose} title="Stäng">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="portal-modal-body">
          <div className="portal-patient-info">
            <span className="patient-name">{patient.firstname} {patient.lastname}</span>
            <span className="patient-pnr">{formatPnrPasis(patient.personnummer, patient.id_type)}</span>
          </div>

          {result ? (
            <div className="portal-sent-summary">
              <div className="portal-success">
                <i className="fas fa-check-circle"></i> Formuläret "{result.item.title}" är skickat till patienten.
              </div>
              {notification?.channel !== 'none' && (
                notification?.sent ? (
                  <div className="portal-success">
                    <i className="fas fa-paper-plane"></i>{' '}
                    {notification.channel === 'sms' ? 'SMS' : 'E-post'} med länk skickat.
                  </div>
                ) : (
                  <div className="portal-error">
                    <i className="fas fa-exclamation-triangle"></i>{' '}
                    Notisen ({notification.channel === 'sms' ? 'SMS' : 'e-post'}) kunde inte skickas: {notification.error}.
                    Formuläret finns ändå i patientens Bokningsapp.
                  </div>
                )
              )}
              <div className="portal-link-row">
                <span className="label">Länk till formuläret:</span>
                <code className="portal-link">{result.link}</code>
              </div>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label><i className="fas fa-file-alt"></i> Formulär</label>
                <select
                  value={formRef}
                  onChange={e => setFormRef(e.target.value)}
                  disabled={sending || loadingTemplates}
                >
                  {loadingTemplates && <option value="">Hämtar mallar...</option>}
                  {!loadingTemplates && templates.length === 0 && (
                    <option value="">Inga formulärmallar finns</option>
                  )}
                  {templates.some(t => t.is_global) && (
                    <optgroup label="Standardformulär">
                      {templates.filter(t => t.is_global).map(t => (
                        <option key={t.form_ref} value={t.form_ref}>{t.title}</option>
                      ))}
                    </optgroup>
                  )}
                  {templates.some(t => !t.is_global) && (
                    <optgroup label="Egna formulär">
                      {templates.filter(t => !t.is_global).map(t => (
                        <option key={t.form_ref} value={t.form_ref}>{t.title}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {selected && (
                  <div className="portal-template-hint">
                    {selected.description} {selected.question_count} frågor.
                    {selected.scoring?.method && selected.scoring.method !== 'none' && (
                      <> Poäng räknas ut automatiskt{selected.scoring.label ? ` (${selected.scoring.label})` : ''}.</>
                    )}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label><i className="fas fa-bell"></i> Notis till patienten</label>
                <div className="portal-notify-options">
                  <label className="portal-radio">
                    <input type="radio" name="notify" value="sms" checked={notify === 'sms'}
                      onChange={() => setNotify('sms')} disabled={sending} />
                    SMS
                  </label>
                  <label className="portal-radio">
                    <input type="radio" name="notify" value="email" checked={notify === 'email'}
                      onChange={() => setNotify('email')} disabled={sending} />
                    E-post
                  </label>
                  <label className="portal-radio">
                    <input type="radio" name="notify" value="none" checked={notify === 'none'}
                      onChange={() => setNotify('none')} disabled={sending} />
                    Ingen notis
                  </label>
                </div>
              </div>

              {notify === 'sms' && (
                <div className="form-group">
                  <label><i className="fas fa-phone"></i> Telefonnummer</label>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder="07X XXX XX XX" disabled={sending} />
                </div>
              )}
              {notify === 'email' && (
                <div className="form-group">
                  <label><i className="fas fa-envelope"></i> E-postadress</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="namn@exempel.se" disabled={sending} />
                </div>
              )}

              <p className="portal-hint">
                Patienten öppnar formuläret i Bokningsappen genom att logga in med sitt personnummer.
                Svaret kommer tillbaka hit i patientvyn.
              </p>

              {error && (
                <div className="portal-error">
                  <i className="fas fa-exclamation-circle"></i> {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="portal-modal-footer">
          {result ? (
            <button className="btn btn-primary" onClick={onClose}>Stäng</button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Avbryt</button>
              <button
                className="btn btn-primary"
                onClick={handleSend}
                disabled={sending || loadingTemplates || !formRef}
              >
                {sending ? (
                  <><i className="fas fa-spinner fa-spin"></i> Skickar...</>
                ) : (
                  <><i className="fas fa-paper-plane"></i> Skicka formulär</>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendFormModal;
